import { ApiEnvelope, ChatMessage, WidgetConfig, WidgetProduct } from "./types";
import { getWidgetToken } from "./widget-key";

class WidgetApiError extends Error {
  constructor(
    message: string,
    public readonly statusCode: number
  ) {
    super(message);
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  // No x-api-key here on purpose: /public/widget/* is exempt from the
  // platform API key on the backend (see requireApiKey), since this bundle
  // runs in any visitor's browser and a shared platform-wide secret can't be
  // shipped there.
  //
  // X-Koomarce-Widget-Token carries the short-lived session token the
  // loader minted server-side (see widget-key.ts) - this, not the bare
  // publicKey in the path, is what the backend actually authorizes on
  // (see widget-session-token.ts). It expires on its own, so a copied page
  // stops working shortly after; a fresh one can only be minted by whoever
  // holds the connection's secret key, which never reaches this bundle.
  const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}${path}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      "X-Koomarce-Widget-Token": getWidgetToken() ?? "",
      ...init.headers,
    },
  });

  const body = (await response.json().catch(() => null)) as ApiEnvelope<T> | null;

  if (!response.ok || !body?.success) {
    throw new WidgetApiError(
      body?.message || "Request failed",
      response.status
    );
  }

  return body.data;
}

/**
 * Widget-scoped public API, mounted at /api/v1/public/widget in
 * koomarce-client-backend-service (conversations.public.routes.ts).
 * getConfig/getMessages/sendMessage are real and working
 * (conversations.public.service.ts). getProducts has no backend endpoint
 * yet - calling it will 404 until that's built; ShopTab already handles
 * that as its normal error state.
 */
export const widgetApi = {
  getConfig(publicKey: string) {
    return request<WidgetConfig>(`/public/widget/${publicKey}/config`);
  },

  getProducts(publicKey: string) {
    return request<{ products: WidgetProduct[] }>(
      `/public/widget/${publicKey}/products`
    );
  },

  getMessages(publicKey: string, externalCustomerId: string) {
    return request<{ messages: ChatMessage[] }>(
      `/public/widget/${publicKey}/messages?externalCustomerId=${encodeURIComponent(
        externalCustomerId
      )}`
    );
  },

  sendMessage(
    publicKey: string,
    payload: { externalCustomerId: string; content: string }
  ) {
    return request<{ message: ChatMessage }>(
      `/public/widget/${publicKey}/messages`,
      {
        method: "POST",
        body: JSON.stringify(payload),
      }
    );
  },
};

export { WidgetApiError };
